import { Link } from "react-router-dom";
import { ArrowLeft, Mail, FileText, CalendarCheck, Video, Receipt } from "lucide-react";
import { usePraxis } from "../context/PraxisContext";

const TERMIN_BETREFF = "Terminvereinbarung Videosprechstunde Menopause";
const TERMIN_BODY = `Sehr geehrte Frau Dr. Scheer,

ich möchte gerne Ihre Videosprechstunde in Anspruch nehmen und einen Termin mit Ihnen vereinbaren.

Ich freue mich auf ein persönliches und vertrauliches Gespräch und darauf, meine Fragen und Anliegen mit Ihnen besprechen zu können.

Bitte teilen Sie mir mit, welche Termine möglich sind und ob Sie vorab noch weitere Informationen von mir benötigen.

Meine Kontaktdaten:

Name:
Telefonnummer:
eMail:

Vielen Dank für Ihre Rückmeldung.

Mit freundlichen Grüßen
`;

function makeSteps(email) {
  return [
    {
      icon: Mail,
      title: "Anfrage per E-Mail",
      text: (
        <>
          Schreiben Sie mir an{" "}
          <a href={`mailto:${email}`}>{email}</a> oder nutzen Sie die
          Buttons „Termin vereinbaren&#8220; – ich melde mich schnellstmöglich bei Ihnen.
        </>
      )
    },
    {
      icon: FileText,
      title: "Vorab-Informationen",
      text: "Sie erhalten ausführliche Infos und ggf. eine Laboranforderung für Ihren Hausarzt."
    },
    {
      icon: CalendarCheck,
      title: "Termin vereinbaren",
      text: "Wir finden gemeinsam einen Termin, der zu Ihrem Alltag passt."
    },
    {
      icon: Video,
      title: "Videogespräch",
      text: "Ein vertrauliches Gespräch – individuell, ganzheitlich, ohne Eile und in Ihrer vertrauten Umgebung."
    },
    {
      icon: Receipt,
      title: "Abrechnung nach GOÄ",
      text: "Die Leistung wird privatärztlich nach den Analogziffern der Gebührenordnung für Ärzte (GOÄ) abgerechnet."
    }
  ];
}

export default function Wechseljahre() {
  const { settings: PRAXIS } = usePraxis();
  const STEPS = makeSteps(PRAXIS.email);
  const mailto = `mailto:${PRAXIS.email}?subject=${encodeURIComponent(TERMIN_BETREFF)}&body=${encodeURIComponent(TERMIN_BODY)}`;

  return (
    <>
      {/* HERO */}
      <section className="wj-hero" data-testid="wj-hero">
        <div className="wj-hero-inner">
          <Link
            to="/"
            className="btn btn-outline"
            style={{ marginBottom: "1.5rem" }}
            data-testid="wj-back-btn"
          >
            <ArrowLeft size={16} /> Zurück zur Startseite
          </Link>
          <p className="section-label">Neu in der Praxis</p>
          <h1>
            Menopause &amp; Wechseljahre<br />
            <em>Videosprechstunde</em>
          </h1>
          <p className="wj-lead">
            Das heutige Gesundheitssystem lässt für das so wichtige Thema Wechseljahre
            leider oft nicht die Zeit, die es verdient. Deshalb biete ich eine private
            Videosprechstunde an – damit ich mich ganz individuell und ohne Zeitdruck um
            Sie kümmern kann.
          </p>
          <a href={mailto} className="btn btn-gold" data-testid="wj-termin-btn">
            <Mail size={16} /> Termin vereinbaren
          </a>
        </div>
      </section>

      {/* MENOPAUSE VERSTEHEN */}
      <section className="wj-section wj-understand" data-testid="wj-understand-section">
        <div className="section-inner" style={{ maxWidth: 820, margin: "0 auto" }}>
          <div className="section-head">
            <p className="section-label">Hintergrund</p>
            <h2 className="section-title">
              Menopause <em>verstehen</em><br />
              Beschwerden erkennen und gezielt behandeln
            </h2>
          </div>

          <div className="wj-prose">
            <p>
              Stimmungsschwankungen, ein veränderter Zyklus, Schlafprobleme oder ein
              Körpergefühl, das plötzlich anders ist als früher – viele Frauen bemerken
              diese Veränderungen bereits Anfang 40, ohne zu wissen, dass die
              Perimenopause (die Übergangsphase vor der Menopause) möglicherweise schon
              begonnen hat.
            </p>
            <p>
              Auch Beschwerden wie Hitzewallungen, Gelenkbeschwerden, Erschöpfung oder
              anhaltende Schlafstörungen werden leider noch häufig als etwas abgetan,
              das Frauen einfach „aushalten müssen&#8220;.
            </p>

            <blockquote className="wj-quote">Das müssen Sie nicht.</blockquote>

            <p>
              Die Wechseljahre sind eine natürliche Lebensphase – aber sie müssen nicht
              bedeuten, dass Sie sich mit belastenden Beschwerden arrangieren müssen.
            </p>
            <p>
              In unserer Videosprechstunde nehme ich mir Zeit, Ihre individuelle
              Situation ganzheitlich zu betrachten. Im Vorfeld können Sie – in Absprache
              mit Ihrem Hausarzt – relevante Hormonwerte und gegebenenfalls
              Mikronährstoffe bestimmen lassen.
            </p>
            <p>
              Nach einer ausführlichen Anamnese Ihrer Beschwerden, Ihrer Lebenssituation
              und unter Berücksichtigung Ihrer Blutwerte entsteht ein umfassendes Bild.
              Gemeinsam entwickeln wir eine auf Sie abgestimmte Strategie – beispielsweise
              mit bioidentischen Hormonen, Mikronährstoffen und gezielten Veränderungen im
              Lebensstil.
            </p>
            <p>
              Nach etwa 6–8 Wochen besprechen wir in einem weiteren Termin Ihre
              Entwicklung, überprüfen die bisherigen Maßnahmen und passen die
              Empfehlungen individuell an.
            </p>
            <p>
              Gerne erhalten Sie vorab ausführliche Informationen per E-Mail.
            </p>
            <p>
              Mit über 20 Jahren Erfahrung als Gynäkologin und einem ganzheitlichen Blick
              auf die Frau begleite ich Sie dabei, Ihre Beschwerden besser zu verstehen,
              Klarheit zu gewinnen und einen Weg zu finden, der zu Ihnen und Ihrem Leben
              passt.
            </p>
          </div>
        </div>
      </section>

      {/* SO FUNKTIONIERT ES */}
      <section className="wj-section wj-ablauf" data-testid="wj-ablauf-section">
        <div className="section-inner" style={{ maxWidth: 1080, margin: "0 auto" }}>
          <div className="section-head">
            <p className="section-label">Ablauf</p>
            <h2 className="section-title">
              In <em>4 einfachen</em> Schritten
            </h2>
            <p className="lead">
              Von Ihrer Anfrage bis zum vertraulichen Gespräch – so läuft Ihre private
              Videosprechstunde ab.
            </p>
          </div>

          <ol className="wj-steps" data-testid="wj-steps">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <li className="wj-step" key={i} data-testid={`wj-step-${i + 1}`}>
                  <div className="wj-step-num">{i + 1}</div>
                  <div className="wj-step-icon">
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                  <div className="wj-step-body">
                    <h3>{s.title}</h3>
                    <p>{s.text}</p>
                  </div>
                </li>
              );
            })}
          </ol>

          {/* dezenter Hinweis zur Abrechnung */}
          <div className="wj-fineprint" data-testid="wj-fineprint">
            <p>
              Wir rechnen nach Analogziffern der GOÄ ab: Erstanamnese &amp; Beratung 142,94&nbsp;€<br />
              Folgetermin mit Beratung 75,15&nbsp;€.
            </p>
            <p>
              Eine Übernahme durch Ihre private Krankenversicherung sollte von Ihnen bitte
              im Vorfeld abgeklärt werden.
            </p>
          </div>

          <div style={{ textAlign: "center", marginTop: "3rem" }}>
            <a href={mailto} className="btn btn-gold" data-testid="wj-bottom-cta">
              <Mail size={16} /> Termin vereinbaren
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
